import { useDraggable } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import {
	getGetMyCarriersListQueryKey,
	useRemoveCarrierPartyRelation,
} from '@uturn/api/v1';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	cn,
	Separator,
} from '@uturn/ui-kit';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Draggable } from '../draggable';

type Carrier = {
	id: number;
	name: string;
	email: string;
	matches?: number;
};

type Props = {
	carrier: Carrier;
	draggable?: boolean;
	id: string;
};

const ListItem: React.FC<Props> = ({ id, carrier, draggable = false }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id,
		data: {
			title: carrier.name,
			subtitle: carrier.email,
			actualId: carrier.id,
		},
	});
	const { mutate, isPending } = useRemoveCarrierPartyRelation();

	const removeContractedCarrier = () => {
		mutate(
			{
				data: {
					carrierPartyId: carrier.id,
				},
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: getGetMyCarriersListQueryKey(),
					});
				},
				onError: (error) => {
					console.error(error);
				},
			},
		);
	};

	return (
		<Draggable>
			<div
				ref={setNodeRef}
				{...attributes}
				className={cn(
					'flex gap-1 items-center justify-between hover:bg-gray-100 rounded-md p-2 cursor-default z-100 relative',
					(isDragging || isPending) && 'opacity-25',
				)}
			>
				<div className="flex gap-1 items-center">
					{draggable && <Draggable.Handle {...listeners} />}
					<div>
						<Draggable.Title>{carrier.name}</Draggable.Title>
						<div className="flex items-center gap-2">
							<Draggable.Info>{carrier.name}</Draggable.Info>
							{carrier.matches !== undefined && carrier.matches !== 0 && (
								<>
									<Separator orientation="vertical" className="h-6" />
									<Draggable.Info>
										{carrier.matches === 1
											? t(
													'carrier-list.list-item.singular-match',
													'{{ count }} match',
													{
														count: carrier.matches,
													},
												)
											: t(
													'carrier-list.list-item.multiple-matches',
													'{{ count }} matches',
													{
														count: carrier.matches,
													},
												)}
									</Draggable.Info>
								</>
							)}
						</div>
					</div>
				</div>
				<AlertDialog>
					<AlertDialogTrigger asChild>
						<Draggable.Trash />
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t('modal.alert.confirm.title', 'Are you sure?')}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t(
									'modal.alert.delete-carrier.description',
									'This will delete "{{ name }}" as your preferred carrier, this action can not be undone.',
									{
										name: carrier.name,
									},
								)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t('modal.alert.confirm.cancel', 'Cancel')}
							</AlertDialogCancel>
							<AlertDialogAction onClick={() => removeContractedCarrier()}>
								{t('modal.alert.confirm.delete', 'Yes, Delete')}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</Draggable>
	);
};

export default ListItem;
